import { useEffect, useRef } from "react";
import type { MenschView } from "@bing/engine-mensch";
import { playSfx } from "../sound/sfx";

/**
 * The منچ sound cues. Wired ONCE inside `MenschTable`, so it covers BOTH the solo
 * (`MenschGame`) and online (`MenschOnlineGame`) modes — the pattern the card, chess and
 * backgammon tables all use.
 *
 * Every cue ADOPTS-AT-MOUNT SILENTLY: the first render only records a baseline, so a
 * mid-game RESUME or a reconnect (which mounts the table with a die already showing and
 * tokens already out) never replays a burst of sounds.
 *
 * WHAT MAKES A NOISE — all of it BOARD-GAME sound. منچ has no cards in it, so it plays no
 * card clips: the deal flick and the trick win/lose clips it used to borrow were simply the
 * wrong instrument, however pleasant.
 *  - a THROW → `diceRoll`;
 *  - then exactly ONE cue for what the move did, in priority order:
 *      capture → `pieceHit` · reached home → `tokenHome` · left the yard → `tokenEnter` ·
 *      ordinary move → `piecePlace`;
 *  - NOTHING when the turn changes hands (see the note below). Nothing follows a roll either: the dice
 *    are the roll's only voice.
 *
 * ONE EVENT, ONE SOUND. Being captured used to fire `pieceHit` AND a second "you lost it"
 * clip together — two sounds for one thing. The capture ANNOUNCEMENT line already says who
 * hit whom, so the impact needs no second voice.
 *
 * TIMING: every cue that is a CONSEQUENCE of the throw waits for the die to actually LAND
 * on the felt. Firing a consequence at 0ms inverts the order — the turn chime beats the dice
 * onto the felt. This is the exact bug backgammon shipped and had to fix; do not "simplify"
 * these delays away.
 *
 * `playSfx` already no-ops when game sound is off, the table is muted, or the master volume
 * is zero — so this honours every mute and is inert in tests that don't opt sound in.
 */

/**
 * Everything the cues actually key on. It used to carry four more fields (our finished
 * count, our yard count, every seat's yard count, whose move it is) that were recomputed on
 * EVERY frame and read by nothing: the cues moved to the engine's authoritative `lastEvent`
 * + the monotonic counters, and the position-diffing they existed for went with them.
 */
interface MenschSfxSnapshot {
  /** Monotonic throw counter — the ONLY honest "a die was thrown" signal (a repeat value
   *  is still a new throw, which is why the die's value cannot be used here). */
  readonly rollCount: number;
}

function snapshot(view: MenschView): MenschSfxSnapshot {
  return { rollCount: view.rollCount };
}

export function useMenschSfx(view: MenschView): void {
  const first = useRef(true);
  const prev = useRef<MenschSfxSnapshot>(snapshot(view));
  /** The MOVE COUNTER we have already sounded. Keyed on the counter, not the event's
   *  identity — the event is a fresh object on every frame, so identity re-played the cue on
   *  the next unrelated update. */
  const prevEventRef = useRef<number>(view.moveCount);
  // Pending delayed cues, cleared on unmount so a sound can never fire into the lobby.
  const timers = useRef<number[]>([]);
  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    },
    [],
  );

  useEffect(() => {
    const cur = snapshot(view);
    if (first.current) {
      first.current = false;
      prev.current = cur;
      return; // silent adopt — a resume never replays
    }
    const p = prev.current;
    const cue = (name: Parameters<typeof playSfx>[0], delayMs: number): void => {
      if (delayMs <= 0) {
        playSfx(name);
        return;
      }
      timers.current.push(window.setTimeout(() => playSfx(name), delayMs));
    };

    // A THROW — keyed on the monotonic counter, so a repeated value still clatters.
    const threw = cur.rollCount > p.rollCount;
    // The dice clip plays from the START of the throw, NOT at the contact beat. It is a
    // ~1.3s recording of dice tumbling and THEN landing, with its impact 0.70s in — the same
    // moment the die visually hits the felt. Starting it at contact put the audible clatter
    // ~950ms after the die had already stopped, which is what read as "the sound is delayed".
    // Its rattle now runs under the visual tumble and its impact lands with the die.
    if (threw) cue("diceRoll", 0);

    // ONE cue per move, in priority order. `lastEvent` is the engine's authoritative account
    // of what just happened — never inferred by diffing positions.
    const ev = view.lastEvent;
    const movedNow = view.moveCount !== prevEventRef.current;
    prevEventRef.current = view.moveCount;
    if (movedNow && ev) {
      // Exactly one, in priority order.
      if (ev.captured) playSfx("pieceHit");
      // ITEM 5: `enteredHome`, not `finished`. `finished` is only step 43 (the innermost
      // square), so three of every four arrivals were silent.
      else if (ev.enteredHome) playSfx("tokenHome");
      else if (ev.released) playSfx("tokenEnter");
      else playSfx("piecePlace");
    }

    // ITEM 4 — منچ has NO "it's your turn" chime, deliberately.
    //
    // It used to fire on every hand-over. A منچ turn is one tap, so on a four-handed table that
    // was several chimes a minute, and the operator called it annoying — correctly. The board
    // already says whose turn it is (the live hint, and «تاس بریز» lighting up), and the dice
    // clip announces the only turn that needs announcing: yours, the moment you act on it.
    // (تخته نرد dropped its own cue for the same reason on 2026-08-15 — no board game has one.)
    //
    // A dead roll gets no extra sound either: the red ⚠️ alert says it and the turn self-passes.

    prev.current = cur;
  }, [view]);
}
